import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';

import { getIsFSEntityFile } from '@repo/lib/fs/getIsFSEntityFile';
import { useCallback, useEffect } from 'react';

import { useFilesStorage } from '@/store/files-store';

interface UseFsKeyboardNavigationProps {
  /** Combined list of items (files + directories) in display order */
  items: (DirectoryPublic | FilePublicWithPermissions)[];
  /** Whether the hook is enabled */
  enabled?: boolean;
  /** Callback when an item should be opened (Enter key) */
  onOpen?: (item: DirectoryPublic | FilePublicWithPermissions) => void;
}

export function useFsKeyboardNavigation({ items, enabled = true, onOpen }: UseFsKeyboardNavigationProps): void {
  const { setSelected, resetSelected, focusedIndex, anchorIndex, setFocusedIndex, setAnchorIndex, onFSEntityClick } = useFilesStorage();

  // Select all items
  const selectAll = useCallback(() => {
    const files: FilePublicWithPermissions[] = [];
    const directories: DirectoryPublic[] = [];

    for (const item of items) {
      if (getIsFSEntityFile(item)) {
        files.push(item as FilePublicWithPermissions);
      } else {
        directories.push(item as DirectoryPublic);
      }
    }

    setSelected({ files, directories });
    setFocusedIndex(items.length - 1);
    setAnchorIndex(0);
  }, [items, setSelected, setFocusedIndex, setAnchorIndex]);

  // Select range of items from anchor to targetIndex (does NOT update anchor)
  const selectRange = useCallback(
    (fromIndex: number, toIndex: number) => {
      const start = Math.min(fromIndex, toIndex);
      const end = Math.max(fromIndex, toIndex);

      const files: FilePublicWithPermissions[] = [];
      const directories: DirectoryPublic[] = [];

      for (let i = start; i <= end; i++) {
        const item = items[i];
        if (!item) continue;

        if (getIsFSEntityFile(item)) {
          const file = item as FilePublicWithPermissions;
          if (!files.some((f) => f.id === file.id)) {
            files.push(file);
          }
        } else {
          const dir = item as DirectoryPublic;
          if (!directories.some((d) => d.id === dir.id)) {
            directories.push(dir);
          }
        }
      }

      setSelected({ files, directories });
      setFocusedIndex(toIndex);
      // Note: anchor is NOT updated here - it stays at the original selection point
    },
    [items, setSelected, setFocusedIndex]
  );

  // Select a single item (updates anchor point)
  const selectItem = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item) return;

      const isFile = getIsFSEntityFile(item);
      setSelected({
        files: isFile ? [item as FilePublicWithPermissions] : [],
        directories: !isFile ? [item as DirectoryPublic] : [],
      });
      setFocusedIndex(index);
      // Update anchor point for future shift selections
      setAnchorIndex(index);
    },
    [items, setSelected, setFocusedIndex, setAnchorIndex]
  );

  // Move to next item
  const moveNext = useCallback(
    (withShift: boolean) => {
      const currentIndex = focusedIndex ?? -1;
      const nextIndex = currentIndex < 0 ? 0 : Math.min(currentIndex + 1, items.length - 1);

      if (withShift) {
        // Use store's anchorIndex for range selection
        const anchor = anchorIndex ?? currentIndex;
        if (anchor < 0) {
          selectItem(nextIndex);
        } else {
          selectRange(anchor, nextIndex);
        }
      } else {
        selectItem(nextIndex);
      }
    },
    [focusedIndex, anchorIndex, items.length, selectItem, selectRange]
  );

  // Move to previous item
  const movePrev = useCallback(
    (withShift: boolean) => {
      const currentIndex = focusedIndex ?? -1;
      const prevIndex = currentIndex < 0 ? 0 : Math.max(currentIndex - 1, 0);

      if (withShift) {
        // Use store's anchorIndex for range selection
        const anchor = anchorIndex ?? currentIndex;
        if (anchor < 0) {
          selectItem(prevIndex);
        } else {
          selectRange(anchor, prevIndex);
        }
      } else {
        selectItem(prevIndex);
      }
    },
    [focusedIndex, anchorIndex, selectItem, selectRange]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled || items.length === 0) return;

      // Check if we're in an input/textarea
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const currentIndex = focusedIndex ?? -1;

      switch (event.key) {
        case 'a':
        case 'A':
          // Ctrl/Cmd + A: Select all
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            selectAll();
          }
          break;

        case 'Escape':
          // Escape: Deselect all
          event.preventDefault();
          resetSelected();
          break;

        // ArrowRight and ArrowDown both move to next item
        case 'ArrowRight':
        case 'ArrowDown': {
          event.preventDefault();
          moveNext(event.shiftKey);
          break;
        }

        // ArrowLeft and ArrowUp both move to previous item
        case 'ArrowLeft':
        case 'ArrowUp': {
          event.preventDefault();
          movePrev(event.shiftKey);
          break;
        }

        case 'Enter': {
          // Open the focused item
          if (currentIndex >= 0 && currentIndex < items.length) {
            const item = items[currentIndex];
            if (item && onOpen) {
              event.preventDefault();
              onOpen(item);
            }
          }
          break;
        }

        case ' ': {
          // Space: Toggle selection of focused item
          if (currentIndex >= 0 && currentIndex < items.length) {
            const item = items[currentIndex];
            if (item) {
              event.preventDefault();
              onFSEntityClick({
                file: getIsFSEntityFile(item) ? (item as FilePublicWithPermissions) : undefined,
                directory: !getIsFSEntityFile(item) ? (item as DirectoryPublic) : undefined,
                mode: 'toggle',
                itemIndex: currentIndex,
              });
            }
          }
          break;
        }

        default:
          break;
      }
    },
    [enabled, items, focusedIndex, selectAll, resetSelected, moveNext, movePrev, onOpen, onFSEntityClick]
  );

  // Attach keyboard listeners
  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);
}
